/*
 * DATA_RIGHTS: HONEYWELL CONFIDENTIAL AND PROPRIETARY
 * THIS WORK CONTAINS VALUABLE CONFIDENTIAL AND PROPRIETARY
 * INFORMATION. DISCLOSURE, USE OR REPRODUCTION OUTSIDE OF
 * HONEYWELL INTERNATIONAL, INC. IS PROHIBITED EXCEPT AS
 * AUTHORIZED IN WRITING. THIS UNPUBLISHED WORK IS PROTECTED BY
 * THE LAWS OF THE UNITED STATES AND OTHER COUNTRIES. IN THE
 * EVENT OF PUBLICATION, THE FOLLOWING NOTICE SHALL APPLY:
 * Copyright (C) 2022 HONEYWELL INTERNATIONAL, INC., ALL RIGHTS RESERVED.
 *
 * Created by H144765 on 4/4/2022.
 */

import * as d3 from "d3"
import * as tip from "d3-tip"

const WorkflowTree = (intl) => {

    const margin = {top: 30, right: 30, bottom: 30, left: 30};
    let i = 0;
    const durationSlow = 750;
    let root = null;
    let tree = null;
    let firstTime = true;
    let tooltip = null;
    let tooltipTimeout = null;
    let linkTooltip = null;
    let group = null;
    let currentZoom = d3.zoomIdentity;

    function build(dwpData) {
        if (firstTime === true) {
            firstTime = false;

            const width = d3.select("#dwpContainer").style('width').slice(0, -2) - margin.left - margin.right;
            const height = window.innerHeight - document.getElementById("dwpContainer").getBoundingClientRect().top - margin.top - margin.bottom - 40;

            const svg = d3.select("#dwpContainer").append("svg");
            svg.attr("width", width + margin.right + margin.left);
            svg.attr("height", height + margin.top + margin.bottom);

            tree = d3.tree().size([width, height]);

            group = svg.append("g")
                .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

            tooltip = tip.default()
                .attr('class', 'dwpTooltip')
                .offset([-10, 0])
                .html(function(d) {
                    let html = "<span>" + intl.formatMessage({id: "dwp.node." + d.data.nodeType}) + "</span>"
                    if (d.data.actionName != null) {
                        html += "<div class=\"action-name\">" + d.data.actionName + "</div>"
                    }
                    return html
                })
            svg.call(tooltip);

            linkTooltip = tip.default()
                .attr('class', 'dwpTooltip')
                .html(function(d) {
                    return "<span>" + d.data.result === 'FIXED' || d.data.result === 'NOT_FIXED' ? intl.formatMessage({id: "dwp.node.result." + d.data.result}) : d.data.result + "</span>"
                })
            svg.call(linkTooltip);

            //invisible circle that will be placed at cursor position as tooltip target when mouseover link
            svg.append('circle').attr('id', 'tipfollowscursor')

            root = d3.hierarchy(dwpData, function (d) {
                return d.childNodes;
            });

            root.x0 = 0;
            root.y0 = width / 2;

            // big rect overlay to capture and handle zoom
            group.append("rect")
                .attr('class', 'zoomOverlay')
                .attr("fill", "none")
                .attr("pointer-events", "all")
                .attr("width", width + margin.right + margin.left)
                .attr("height", height + margin.top + margin.bottom)
                .attr("transform", "translate(-" + margin.left + ", -" + margin.top + ")")

            svg.call(d3.zoom()
                .scaleExtent([0.5, 30])
                .on("zoom", zoom));

        }

        update(root)
    }


    function update(source){
        const treeData = tree(root)

        const nodes = renderNode(treeData, source);
        renderNodeTextBox(treeData, source)
        renderLink(treeData, source)
        renderLinkDescription(treeData, source)

        // Store the old positions for transition.
        nodes.forEach(function (d) {
            d.x0 = d.x;
            d.y0 = d.y;
        });

    }

    const renderNode = (treeData, source) => {

        const nodes = treeData.descendants();

        // ****************** Nodes section ***************************
        // Update the nodes...
        const node = group.selectAll('g.node')
            .data(nodes, function (d) {
                return d.id || (d.id = ++i);
            });

        // Enter any new modes at the parent's previous position.
        const nodeEnter = node.enter().append('g')
            .attr('class', 'node');

        nodeEnter
            .attr("transform", function () {
                return "translate(" + currentZoom.apply([source.x0, source.y0]) + ")";
            })
            .on('click', click);

        // Add rect for the nodes
        nodeEnter.append('rect')
            .attr('class', 'node')
            .attr('x', -20)
            .attr('y', -20)
            .attr('width', 40)
            .attr('height', 40)
            .style("fill", function(d) {
                return d._children ? "lightsteelblue" : "#fff";
            })
            .on("mouseover", (d, index, list) => showTooltip(d, index, list))
            .on("mouseout", (d, index, list) => hideTooltip(d, index, list));

        // Add labels for the nodes
        nodeEnter.append('text')
            .attr('class', function(d) {
                if(d.data.nodeType === 'CORRECTIVE_ACTION') {
                    return 'caText';
                } else if(d.data.nodeType === 'END_POINT') {
                    return 'endPointText';
                } else if (d.data.nodeType === 'DEAD_END') {
                    return 'deadEndText';
                } else {
                    return ('testText');
                }
            })
            .attr("y", "15")
            .attr("x", function(d) {
                return d.childNodes || d._children ? 16 : -16;
            })
            .attr("text-anchor", function(d) {
                return d.childNodes || d._children ? "end" : "start";
            })
            .html( function(d) {
                if (d.data.nodeType === 'CORRECTIVE_ACTION') {
                    return '&#xE948;';
                } else if (d.data.nodeType === 'END_POINT') {
                    return '&#xE90f';
                } else if (d.data.nodeType === 'DEAD_END') {
                    return '&#xE900';
                } else {
                    return ('&#xE95e;');
                }
            })
            .on("mouseover", (d, index, list) => showTooltip(d, index, list))
            .on("mouseout", (d, index, list) => hideTooltip(d, index, list));


        // UPDATE
        const nodeUpdate = nodeEnter.merge(node);

        // Transition to the proper position for the node
        nodeUpdate.transition()
            .duration(durationSlow)
            .attr("transform", transform(currentZoom));

        // Update the node attributes and style
        nodeUpdate.select('rect.node')
            .attr('width', 40)
            .attr('height', 40)
            .attr('x', -20)
            .attr('y', -20)
            .style("fill", function(d) {
                return d._children ? "lightsteelblue" : "#fff";
            });
        nodeUpdate.select('rect.node')
            .attr('cursor', 'pointer');


        // Remove any exiting nodes
        const nodeExit = node.exit().transition()
            .duration(durationSlow)
            .attr("transform", function () {
                return "translate(" + currentZoom.apply([source.x, source.y]) + ")";
            })
            .remove();

        // On exit reduce the node circles size to 0
        nodeExit.select('rect')
            .attr('width', 40)
            .attr('height', 40)
            .attr('x', -20)
            .attr('y', -20);

        // On exit reduce the opacity of text labels
        nodeExit.select('text')
            .style('fill-opacity', 1e-6);

        return(nodes);
    }


    function renderNodeTextBox(treeData, source) {
        const nodeBoxes = treeData.descendants();

        const nodeBox = group.selectAll('g.textBox')
            .data(nodeBoxes, function (d) {
                return d.id || (d.id = ++i);
            });

        const textBoxEnter = nodeBox.enter().append('g')
            .attr('class', 'textBox')
            .attr("transform", nodeTextTransform(currentZoom, source))

        const text = textBoxEnter.append('text')
            .attr('class', 'textBox')
            .attr("y", 0)
            .attr("dy", 0)
            .attr("x", 25)
            .attr("text-anchor", "start")
            .attr("font-size", "0.85em")
            .html(function (d) {
                if (d.data.nodeType === 'DEAD_END' || d.data.nodeType === 'END_POINT') {
                    return intl.formatMessage({id: "dwp.node." + d.data.nodeType})
                } else {
                    return d.data.actionName
                }
            });

        const textBoxUpdate = textBoxEnter.merge(nodeBox);

        text.call(wrap, 300)

        // Transition to the proper position for the node
        textBoxUpdate.transition()
            .duration(durationSlow)
            .attr("transform", nodeTextTransform(currentZoom));

        const textBoxExit = nodeBox.exit().transition()
            .duration(durationSlow)
            .attr("transform", nodeTextTransform(currentZoom, source))
            .remove();
        // On exit reduce the opacity of text labels
        textBoxExit.select('text')
            .style('fill-opacity', 1e-6);
    }

    function renderLink(treeData, source) {
        const links = treeData.descendants().slice(1);

        // ****************** links section ***************************

        // Update the links...
        const link = group.selectAll('polyline.link')
            .data(links, function (d) {
                return d.id;
            });

        // Enter any new links at the parent's previous position.
        const linkEnter = link.enter().insert('polyline', "g")
            .attr("class", "link")
            .attr('points', function () {
                const o = {y: source.y0, x: source.x0}
                return linkPolyline(o, o, currentZoom)
            })
            .on("mouseover", (d, index, list) => {
                const target = d3.select('#tipfollowscursor')
                    .attr('cx', d3.event.offsetX)
                    .attr('cy', d3.event.offsetY - 8) // 8 pixels above the cursor
                    .node();
                showTooltip(d, index, list, target)
            })
            .on("mouseout", (d, index, list) => hideTooltip(d, index, list, true));

        // UPDATE
        const linkUpdate = linkEnter.merge(link);

        // Transition back to the parent element position
        linkUpdate.transition()
            .duration(durationSlow)
            .attr('points', function(d) { return linkPolyline(d, d.parent, currentZoom)})

        // Remove any exiting links
        const linkExit = link.exit().transition()
            .duration(durationSlow)
            .attr('points', function () {
                const o = {y: source.y0, x: source.x0}
                return linkPolyline(o, o, currentZoom)
            })
            .remove();


        linkExit.select('polyline')
            .style('stroke-opacity', 0)
    }


    function renderLinkDescription(treeData, source) {

        const linkTexts = treeData.descendants().slice(1);

        // ****************** links description section ***************************

        const linksDescription = group.selectAll('g.linkDescription')
            .data(linkTexts, function (d) {
                return d.id;
            });

        const linksDescriptionEnter = linksDescription.enter().insert('g')
            .attr("class", "linkDescription")
            .attr("transform", function () {
                const o = {y: source.y0, x: source.x0}
                return "translate(" + linkTextPosition2(o, o, currentZoom) + ") scale(0.0, 0.0)";
            })

        const text = linksDescriptionEnter.append('text')
            .attr('x', 0)
            .attr('y', 0)
            .attr('dx', 0)
            .attr('dy', 0)
            .text(function (d) {
                return d.data.result === 'FIXED' || d.data.result === 'NOT_FIXED' ? intl.formatMessage({id: "dwp.node.result." + d.data.result}) : d.data.result;
            });

        text.call(wrap, 200)


        // UPDATE
        const linksDescriptionUpdate = linksDescriptionEnter.merge(linksDescription);

        linksDescriptionUpdate.transition()
            .duration(durationSlow)
            .attr("transform", linkTextTransform(currentZoom))

        const linksDescriptionExit = linksDescription.exit().transition()
            .duration(durationSlow)
            .attr("transform", function () {
                return "translate(" + currentZoom.apply([source.x, source.y]) + ")";
            })
            .remove();
        linksDescriptionExit.select('rect')
            .style('opacity', 0)
            .style('stroke', 'rgb(0,0,0, 0)')

        linksDescriptionExit.select('text')
            .style('fill-opacity', 0)
    }

    const wrap = (text, width) => {
        text.each(function () {
            const localText = d3.select(this)
            const words = localText.text().split(/\s+/).reverse()
            let word
            let line = []
            let lineNumber = 0
            const lineHeight = 1.1 // ems
            const y = localText.attr("y")
            const x = localText.attr("x")
            const dy = parseFloat(localText.attr("dy"))
            let tspan = localText.text(null).append("tspan").attr("x", x).attr("y", y).attr("dy", dy + "em");
            while (word = words.pop()) {
                line.push(word);
                tspan.text(line.join(" "));
                if (tspan.node().getComputedTextLength() > width) {
                    line.pop();
                    tspan.text(line.join(" "));
                    line = [word];
                    tspan = localText.append("tspan").attr("x", x).attr("y", y).attr("dy", ++lineNumber * lineHeight + dy + "em").text(word);
                }
            }
        });
    }

    function click(d) {
// use the following to superficially change the text of the node.
//  this.getElementsByTagName('text')[0].textContent = "clicked all over"

        if (d.children) {
            d._children = d.children;
            d.children = null;
        } else {
            d.children = d._children;
            d._children = null;
        }


        update(d);
    }

    function showTooltip(d, index, list, link) {
        clearTimeout(tooltipTimeout);
        tooltipTimeout = setTimeout(function() {
            if (link) {
                linkTooltip.show(d, link)
            } else {
                tooltip.show.apply(list[index], [d, index, list])
            }
        }, 50);
    }

    function hideTooltip(d, index, list,link) {
        clearTimeout(tooltipTimeout);
        tooltipTimeout = setTimeout(function() {
            if (link) {
                linkTooltip.hide.apply(list[index], [d, index, list])
            } else {
                tooltip.hide.apply(list[index], [d, index, list])
            }
        }, 50);
    }

    function zoom() {
        let duration = 50;  // moving is very fast for good user experience

        if (currentZoom.k !== d3.event.transform.k) {
            duration = 200;    // zooming is slower
        }
        currentZoom = d3.event.transform;

        group.selectAll('g.node')
            .transition()
            .duration(duration)
            .attr("transform", transform(d3.event.transform));
        group.selectAll('polyline.link')
            .transition()
            .duration(duration)
            .attr('points', function(d) { return linkPolyline(d, d.parent, d3.event.transform)})
        group.selectAll('g.linkDescription')
            .transition()
            .duration(duration)
            .attr("transform", linkTextTransform(d3.event.transform))
        group.selectAll('g.textBox')
            .transition()
            .duration(duration)
            .attr("transform", nodeTextTransform(d3.event.transform));
    }

    function transform(t) {
        return function(d) {
            return "translate(" + t.apply([d.x, d.y]) + ")";
        };
    }

    function nodeTextTransform(t, source) {
        return function(d) {
            const height = d3.select(this).node().getBBox().height
            d = source ? source : d
            if(t.k >= 1.7) {
                const zoomApplied = t.apply([d.x, d.y])
                zoomApplied[1] = zoomApplied[1] + 12 - (height / 2)
                return "translate(" + zoomApplied + ")"
            } else {
                return 'translate(' + t.apply([d.x, d.y]) + ') scale(0.0, 0.0)'
            }
        }
    }

    function linkTextTransform(t) {
        return function(d) {
            if(t.k >= 1.7) {
                return "translate(" + linkTextPosition2(d, d.parent, t) + ")";
            } else {
                return 'translate(' + t.apply([d.x, d.y]) + ') scale(0.0, 0.0)'
            }
        }
    }

    function linkPolyline(s, d, e) {
        const points = []
        points.push([s.x*e.k + e.x, s.y*e.k + e.y])
        points.push([s.x*e.k + e.x, d.y*e.k + Math.max(35, 30*e.k/2) + e.y])
        points.push([d.x*e.k + e.x, d.y*e.k + Math.max(35, 30*e.k/2) + e.y])
        points.push([d.x*e.k + e.x, d.y*e.k + e.y])
        return points
    }

    function linkTextPosition2(s, d, e) {
        const x = (s.x * e.k) + e.x + 10;
        const y = ((s.y + d.y) * e.k) / 2 + e.y - 10;
        return [x, y];
    }

    function destroy() {
        d3.selectAll(".dwpTooltip").remove();
        d3.select("svg").remove();
    }

    this.build = build
    this.destroy = destroy

}

export default WorkflowTree;




